import { Http, Headers, Response } from 'app/services/http';
import {
    IAzureResource,
    IAzureBaseResource,
    IAzureResourceCreateResult,
    IAzureResourceShadow,
    IDataSourceCredentialUrlResponse,
    ISOCDataLakeAnalyticsAccount
} from '../interfaces/IAzureResource';
import { AuthService } from './authentication/auth.service';
import { HttpClientServiceBase } from './httpclient.service';
import { LoggerService } from 'app/logger/logger.service';
import { CrudEvent } from 'app/logger/logger.crud-event';
import * as Constants from './globals';
import * as HttpUtil from 'app/shared/utils/http.utils';
import { UrlInformationBaseService } from './url-information-base.service';
import { sleep } from 'app/shared/utils/sleep.util';
import { LoggerHelper } from 'app/logger/logger-helper';
import { retryWhen, timeout } from 'rxjs/operators';
import ShellAPI from 'a365-shell-api';
import { Model } from 'app/model/backendmodel';
import { Trigger } from 'app/model/trigger/trigger';
import {
    AdapterContext,
    IResourceContext,
    LinkTable,
    LinkTopic,
    Metamodel,
    ModelConstants,
    ModelMarker,
    MODEL_SERVICE_LIST_TIMEOUT,
    ModelUtilties
} from 'shell-models-lib';
import { Inject } from '@angular/core';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from 'app/model/services/model.service';

interface IHasDescription {
    description: string;
}

interface IHasType {
    type: string;
}

export interface IAzureUserAssignedManagedIdentities {
    [resourceId: string]: {};
}

export type ManagedIdentityType = 'SystemAssigned' | 'UserAssigned' | 'SystemAssigned,UserAssigned' | 'None';

export interface IPatchPayload {
    identity: {
        // eslint-disable-next-line
        type: ManagedIdentityType;
        userAssignedIdentities: IAzureUserAssignedManagedIdentities;
    };
}

type IPaginatedResource = IAzureResource[] & { value?: IAzureResource[]; nextLink?: string };

export interface IMetadataClient {
    create(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource>;

    createWithRequestId?(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResourceCreateResult>;

    deleteResource(
        resourceType: Constants.ResourceType,
        resourceName: string,
        retryBadRequest?: boolean
    ): Promise<Response>;

    getResource(
        resourceType?: Constants.ResourceType,
        resourceIdentifier?: string,
        omitErrorLog?: boolean,
        options?: { workspaceObjectId?: string; resourceSubType?: string; shouldCache?: boolean; force?: boolean, skipValidation?: boolean }
    ): Promise<IAzureResource>;

    update(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource>;

    rename(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<Response>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    patch(resourceType: Constants.ResourceType, name: string, payload: {}, path?: string): Promise<any>;

    patchWithRequestId?(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResourceCreateResult>;

    listResources(
        resourceType: Constants.ResourceType,
        complexResourceType?: string,
        apiVersion?: string,
        options?: {
            workspaceObjectId?: string;
            shouldCache?: boolean;
            force?: boolean;
            dontValidate?: boolean;
            convertToFabric?: boolean;
        }
    ): Promise<IAzureResource[]>;

    listResourceShadows(
        resourceType: Constants.ResourceType,
        options?: { workspaceObjectId?: string }
    ): Promise<IAzureResourceShadow[]>;

    getFirstPage(
        resourceType: Constants.ResourceType,
        complexResourceType?: string,
        apiVersion?: string,
        options?: { workspaceObjectId?: string; shallowGet?: boolean }
    ): Promise<IPaginatedResource>;

    getNextPage(nextLink: string): Promise<Response>;

    getDataSourceCredentialUrl(lsName: string, sessionId: string): Promise<IDataSourceCredentialUrlResponse>;

    getSparkHistoryEndpoint(baseUri: string): Promise<string>;

    getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string>;

    getBaseUri(): string;

    getBaseResourceUri?(): string;

    getSubIdUri?(): string;

    getAuthArea(): string;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryTriggers(triggerFilter?: Model.TriggerQueryFilter): Promise<any>; // warning: not supported in Synapse, use listResources instead

    getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getUAMI(subid: string, rg: string): Promise<any>;

    upgradeLinkedService(linkedServiceName: string): Promise<Response>;
}

export abstract class BaseMetadataClient extends HttpClientServiceBase implements IMetadataClient {
    protected className = 'BaseMetadataClient';
    protected abstract apiVersion: string;

    public constructor(
        @Inject(RESOURCE_CONTEXT_DATA) protected readonly _contextData: IResourceContext,
        protected readonly httpClient: Http,
        protected readonly authService: AuthService,
        protected readonly urlService: UrlInformationBaseService,
        protected readonly logger: LoggerService,
        protected readonly loggerHelper: LoggerHelper
    ) {
        super(authService, urlService, logger);
    }

    public async create(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource> {
        if (resourceType === ModelConstants.linkConnection) {
            return (await this._updateLinkTopic(name, payload)) as IAzureResource;
        }
        return (await this._createResource(resourceType, name, payload, 'create')) as IAzureResource;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async deleteResource(
        resourceType: Constants.ResourceType,
        resourceName: string,
        retryBadRequest: boolean = false,
        complexResourceType?: string
    ): Promise<Response> {
        return new Promise(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = this.createSingleResourceUri(
                complexResourceType ? complexResourceType : resourceType,
                resourceName
            );
            let status: number;

            return this.httpClient
                .delete(url, {
                    headers,
                    params: this.getDefaultParams(null, this._getApiVersion(resourceType))
                })
                .pipe(retryWhen(retryBadRequest ? HttpUtil.retryPolicyForBadRequest : HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    let pollingResponse: Response;
                    if (resp.status === 202) {
                        try {
                            pollingResponse = await this._pollForCompletion(resp, resourceType);
                        } catch (error) {
                            if (error.status !== 404) {
                                throw error;
                            } else {
                                error.status = 200;
                            }
                        }
                    }

                    status = resp.status;
                    this.logger.logEvent(
                        new CrudEvent('delete', resourceName, '', resourceType, undefined, status.toString())
                    );
                    resolve(pollingResponse || resp);
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(
                        this.className,
                        'deleteResource: Error while calling url',
                        reason,
                        { deleteResourceUrl: url }
                    );
                    status = reason.status;
                    reject(reason);
                });
        });
    }

    public async getResource(
        resourceType?: Constants.ResourceType,
        resourceName?: string,
        omitErrorLog?: boolean
    ): Promise<IAzureResource> {
        return (await this._getResource(resourceType, resourceName, omitErrorLog)) as IAzureResource;
    }

    public async update(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<IAzureResource> {
        if (resourceType === 'linkconnection') {
            return (await this._updateLinkTopic(name, payload)) as IAzureResource;
        }
        return (await this._createResource(resourceType, name, payload, 'update')) as IAzureResource;
    }

    /**
     * Gets the complete list of resources.
     * @param resourceType
     */
    public async listResources(
        resourceType: Constants.ResourceType,
        complexResourceType?: string,
        apiVersion?: string
    ): Promise<IAzureResource[]> {
        return new Promise<IAzureResource[]>(async (resolve, reject) => {
            this._list(resourceType, true, apiVersion)
                .then((response) => {
                    resolve(response as IAzureResource[]);
                })
                .catch((reason) => {
                    reject(reason);
                });
        });
    }

    /**
     * Gets the complete list of resources (with fewer properties).
     * @param resourceType
     */
    public async listResourceShadows(resourceType: Constants.ResourceType): Promise<IAzureResourceShadow[]> {
        return new Promise<IAzureResourceShadow[]>(async (resolve, reject) => {
            this._list(resourceType, false)
                .then(async (response) => {
                    const resourceList = (await response) as IAzureResourceShadow[];
                    resolve(resourceList);
                })
                .catch((reason) => {
                    reject(reason);
                });
        });
    }

    public abstract getDataSourceCredentialUrl(
        lsName: string,
        sessionId: string
    ): Promise<IDataSourceCredentialUrlResponse>;

    public abstract getSparkHistoryEndpoint(baseUri: string): Promise<string>;

    public abstract getSparkHistoryServerVersion(workspace: Model.Workspace): Promise<string>;

    public abstract getSOCDataLakeAnalyticsAccount(workspace: Model.Workspace): Promise<ISOCDataLakeAnalyticsAccount>;

    public abstract rename(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<Response>;

    public async getFirstPage(
        basicResourceType: Constants.ResourceType,
        complexResourceType?: string,
        apiVersion?: string
    ): Promise<IPaginatedResource> {
        const resourceType = complexResourceType ? complexResourceType : basicResourceType;
        return new Promise(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = this._createResourceBaseUri(resourceType);
            this.httpClient
                .get(url, {
                    headers,
                    params: this.getDefaultParams(null, apiVersion || this._getApiVersion(basicResourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    resolve(resp.json());
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, 'getFirstPage: Error while calling url', reason, {
                        getFirstPageUrl: url
                    });
                    reject(reason);
                });
        });
    }

    public async getNextPage(nextLink: string): Promise<Response> {
        const headers = await this.getDefaultHeadersAsync();
        return await this.httpClient
            .get(nextLink, { headers })
            .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
            .toPromise();
    }

    public getAuthArea(): string {
        return this.authArea;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async queryTriggers(triggerFilter: Model.TriggerQueryFilter): Promise<any> {
        return new Promise<Trigger[]>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = `${this.getBaseUri()}/querytriggers`;
            this.httpClient
                .post(url, JSON.stringify(triggerFilter), {
                    headers,
                    params: this.getDefaultParams(null, this.apiVersion)
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    resolve(resp.json());
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, '_list: Error while calling url', reason, {
                        listUrl: url
                    });
                    reject(reason);
                });
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async getUAMI(subid: string, rg: string): Promise<any> {
        const url = `${this.getAzureBaseUri()}/providers/Microsoft.ManagedIdentity/userAssignedIdentities`;
        const headers = await this.getDefaultHeadersAsync();
        return new Promise((resolve, reject) => {
            this.httpClient
                .get(url, { headers, params: this.getDefaultParams(null, '2018-11-30') })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then((res) => resolve(res.json().value))
                .catch((reason) => reject(reason));
        });
    }

    public abstract upgradeLinkedService(linkedServiceName: string): Promise<Response>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async patch(resourceType: Constants.ResourceType, name: string, payload: {}): Promise<any> {
        let status: number;
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = resourceType && name ? this.createSingleResourceUri(resourceType, name) : this.getBaseUri();
            this.httpClient
                .patch(url, JSON.stringify(payload), {
                    headers,
                    params: this.getDefaultParams(null, this._getApiVersion(resourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    status = resp.status;
                    if (status === 202 || (status === 201 && resourceType === 'workspace')) {
                        const finalResp = await this._pollForCompletion(resp, resourceType);

                        // See https://docs.microsoft.com/azure/azure-resource-manager/resource-manager-async-operations#azure-asyncoperation-request-and-response.
                        const body = finalResp.json();
                        if (body && body.status === 'Failed') {
                            throw finalResp;
                        }

                        resolve(finalResp.json());
                    } else {
                        resolve(resp.json());
                    }
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, 'patch: Error while calling url', reason, {
                        createResourceUrl: url
                    });
                    status = reason.status;
                    reject(reason);
                })
                .then(() => {
                    this.logger.logEvent(
                        new CrudEvent(
                            'patch',
                            name || this.urlService.resourceName,
                            resourceType,
                            '',
                            undefined,
                            status.toString()
                        )
                    );
                });
        });
    }

    protected createSingleResourceUri(resourceType: Constants.ResourceType | string, resourceName: string): string {
        return `${this._createResourceBaseUri(resourceType)}/${resourceName}`;
    }

    protected _createResourceBaseUri(resourceType: Constants.ResourceType | string): string {
        let resourcePlural = `${resourceType}s`;
        if (resourceType === 'factory') {
            resourcePlural = 'factories';
        }

        if (resourceType === 'airflowEntity') {
            resourcePlural = 'airflowEntities';
        }

        if (resourceType === ModelConstants.managedPrivateEndpoint) {
            resourcePlural = 'managedVirtualNetworks/default/managedPrivateEndpoints'; // TODO : This will go away once vnet becomes an actual resource
        }
        return `${this.getBaseUri()}/${resourcePlural}`;
    }

    protected _getApiVersion(resourceType: Constants.ResourceType): string {
        return this.apiVersion;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected async _updateLinkTopic(name: string, payload: any): Promise<{}> {
        const modelService = await getServiceInstance<ModelService>(this._contextData, 'ModelService');
        const linkTopicNamed = (
            await modelService
                .list(new RegExp('^' + name + '$'), LinkTopic.MODEL)
                .pipe(timeout(MODEL_SERVICE_LIST_TIMEOUT))
                .toPromise()
        )[0] as LinkTopic;
        if (!__IS_A365__) {
            linkTopicNamed.isSelfPendingPublish = false; // In ADF, topic has been published with ARM.
        }
        const linkTopicUpdateResult = linkTopicNamed.isSelfPendingPublish
            ? await this._createResource('linkconnection', name, payload, 'update')
            : {};
        // If a link topic was deleted (wasn't published) and then a new link topic with the same name was created, we need to remove all the link tables that belong to the old link topic.
        const deletedLinkTopicNamed = Array.from(modelService.deletedNameds).find(
            (named: LinkTopic) => Metamodel.modelOf(named) === LinkTopic.MODEL && named.name === name
        ) as LinkTopic;
        const allLinkTables = (deletedLinkTopicNamed?.linkTables || [])
            .map((linkTable) => {
                ModelMarker.markDelete(linkTable);
                return linkTable;
            })
            .concat(linkTopicNamed?.linkTables || []);
        const changedLinkTables = allLinkTables.filter(
            (linkTable: LinkTable) =>
                ModelMarker.isNew(linkTable) || ModelMarker.isDirty(linkTable) || ModelMarker.isDeleted(linkTable)
        );
        if (changedLinkTables.length) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const changedLinkTableResources: any[] = [];
            for (const linkTable of changedLinkTables) {
                const operationType = ModelMarker.isDeleted(linkTable)
                    ? 'remove'
                    : ModelMarker.isNew(linkTable)
                    ? 'add'
                    : 'update';
                const linkTableResource = await ModelUtilties.toResource(
                    AdapterContext.defaultContextOf(this._contextData),
                    linkTable
                );
                changedLinkTableResources.push(
                    operationType === 'remove'
                        ? {
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              id: (linkTableResource as any).id,
                              operationType
                          }
                        : { ...linkTableResource, operationType }
                );
            }
            await new Promise<void>(async (resolve, reject) => {
                const headers = await this.getDefaultHeadersAsync();
                const basicUrl = this.createSingleResourceUri('linkconnection', name);
                const url = `${basicUrl}/editTables`;
                let status: number;

                this.httpClient
                    .post(
                        url,
                        { linkTables: changedLinkTableResources },
                        {
                            headers,
                            params: { ...this.getDefaultParams(null, this.apiVersion) }
                        }
                    )
                    .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                    .toPromise()
                    .then(async (resp) => {
                        status = resp.status;
                        if (status === 202) {
                            const finalResp = await this._pollForCompletion(resp, 'linkconnection');
                            const body = finalResp.json();
                            if (body && body.status === 'Failed') {
                                throw finalResp;
                            }
                        }
                        resolve();
                    })
                    .catch((reason) => {
                        this.loggerHelper.logRequestError(
                            this.className,
                            '_updateLinkTables: Error while calling url',
                            reason,
                            { createResourceUrl: url }
                        );
                        status = reason.status;
                        reject(reason);
                    })
                    .finally(() => {
                        this.logger.logEvent(
                            new CrudEvent(
                                'updateLinkTables',
                                name || this.urlService.resourceName,
                                'linkconnection',
                                '',
                                undefined,
                                status.toString()
                            )
                        );
                    });
            });
            linkTopicNamed.linkTables = (linkTopicNamed.linkTables || []).filter(
                (linkTable) => !ModelMarker.isDeleted(linkTable)
            );
            linkTopicNamed.linkTables.forEach((linkTable) => ModelMarker.markFetched(linkTable));
            linkTopicNamed.isSelfPendingPublish = false;
        }
        return linkTopicUpdateResult;
    }

    protected async _pollForCompletion(resp: Response, resourceType: Constants.ResourceType): Promise<Response> {
        const locationHeader = resp.headers.get('location') || '';
        // for notebooks or bigDataPool or sqlPool always use Location header.
        let asyncHeaderUrl: string = '';
        if (resourceType === 'notebook' || resourceType === 'bigDataPool' || resourceType === 'sqlPool') {
            asyncHeaderUrl = locationHeader;
        } else if (resourceType === 'workspace') {
            asyncHeaderUrl = resp.headers.get('azure-asyncoperation');
        } else if (resourceType === 'database') {
            // The IDW targeted header would be
            // https://idwservice.<idw-region-host>/FirstParty/synapse/subscriptions/<sub>/resourcegroups/<rg>/workspaces/<workspaceName>/databases/operationResults/<operationid>?api-version=2021-01-01-preview
            const results = locationHeader.match(
                /(.*)idwservice\.(.*)\/FirstParty\/synapse\/(.*)operationResults\/(.*)\?(.*)$/i
            );
            if (results?.[4]) {
                const operationId = results[4];
                asyncHeaderUrl = `${this.getBaseUri()}/${resourceType}s/operations/${operationId}`;
            }
            // The SyMS targeted header would be https://<data-plane-host>/dataabses/operations/<operationid>
            else if (
                /(.*)\/databases\/operations\/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}(.*)/i.test(
                    locationHeader
                )
            ) {
                asyncHeaderUrl = locationHeader;
            }
        } else if (resourceType === 'managedPrivateEndpoint') {
            return resp;
        }

        if (!asyncHeaderUrl) {
            asyncHeaderUrl = `${this.getBaseUri()}/operationResults/${resp.json().operationId}`;
        }
        // asyncHeaderUrl for database is different from the one shown in header.location
        if (!locationHeader.trim().startsWith(asyncHeaderUrl) && resourceType !== 'database') {
            // asyncHeaderUrl may miss some URL parameters, such as api-version. This is added when sending request.

            // By protocol, we should prefer using Location header if possible.
            // For now we keep using local hard-coded one, but also compare response Location header value is different from our local hard-coded one.
            // If there is no such warning as follows shown on PROD environment, we may switch preference to Location header.
            this.logger.warn(BaseMetadataClient.className, '_pollForCompletion: Unexpected Location header value.', {
                resourceType,
                locationHeader,
                asyncHeaderUrl
            });
        }

        // put workspac call returns 201, all subsequent calls - operation status calls return 200 with { status: InProgress/Succeeded }
        const skipDefaultParams =
            resourceType === 'bigDataPool' || resourceType === 'sqlPool' || resourceType === 'workspace';
        while (
            resp.status === 202 ||
            (resourceType === 'workspace' && (resp.status === 201 || resp.status === 200)) ||
            (resourceType === 'database' && (resp.status === 201 || resp.status === 200))
        ) {
            const headers = await this.getDefaultHeadersAsync();
            resp = await this.httpClient
                .get(asyncHeaderUrl, {
                    headers,
                    params: skipDefaultParams ? {} : this.getDefaultParams(null, this.apiVersion)
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();
            if (resourceType === 'workspace' && resp.json().status !== 'InProgress') {
                break;
            } else if (
                resourceType === 'database' &&
                !(resp.json().status === 'InProgress' || resp.json().status === 'Accepted')
            ) {
                break;
            }
            await sleep(1000);
        }

        return resp;
    }

    private async _list(
        resourceType: Constants.ResourceType,
        includeAllProperties?: boolean,
        apiVersion?: string
    ): Promise<IAzureBaseResource[]> {
        return new Promise<IAzureBaseResource[]>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = this._createResourceBaseUri(resourceType);
            this.httpClient
                .get(url, {
                    headers,
                    params: this.getDefaultParams(null, apiVersion || this._getApiVersion(resourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    const mergedList = await this._mergeListResult(resourceType, resp, headers, includeAllProperties);
                    resolve(mergedList);
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(this.className, '_list: Error while calling url', reason, {
                        listUrl: url
                    });
                    reject(reason);
                });
        });
    }

    private async _mergeListResult(
        resourceType: Constants.ResourceType,
        response: Response,
        headers: Headers,
        includeAllProperties?: boolean
    ): Promise<IAzureBaseResource[]> {
        let resultSet: IAzureResource[] = new Array<IAzureResource>();
        let resp = response.json();

        resultSet = this._addToResults(resultSet, resp);

        while (resp.nextLink) {
            const nextResp = await this.httpClient
                .get(resp.nextLink, { headers })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();
            resp = await nextResp.json();
            resultSet = this._addToResults(resultSet, resp);
        }

        if (includeAllProperties) {
            return resultSet;
        }

        return resultSet.map((resource) => {
            return {
                name: resource.name,
                objectType: (resource.properties as IHasType).type
                    ? (resource.properties as IHasType).type
                    : resourceType,
                description: (resource.properties as IHasDescription).description
                    ? (resource.properties as IHasDescription).description
                    : undefined,
                etag: resource.etag
            };
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _addToResults(results: IAzureResource[], response: any): IAzureResource[] {
        if (response.value) {
            results = results.concat(response.value as IAzureResource[]);
        } else if (Array.isArray(response)) {
            results.push(...response);
        } else {
            results.push(response);
        }
        return results;
    }

    private async _getResource(
        subResourceType?: Constants.ResourceType,
        resourceName?: string,
        omitErrorLog?: boolean
    ): Promise<IAzureResource> {
        const headers = await this.getDefaultHeadersAsync();
        const url =
            subResourceType && resourceName
                ? this.createSingleResourceUri(subResourceType, resourceName)
                : this.getBaseUri();

        try {
            const resp = await this.httpClient
                .get(url, {
                    headers,
                    params: this.getDefaultParams(null, this._getApiVersion(subResourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise();
            return resp.json();
        } catch (error) {
            if (!omitErrorLog) {
                this.loggerHelper.logRequestError(this.className, '_getResource: Error while calling url', error, {
                    getResourceUrl: url,
                    stackTrace: ShellAPI.utils.getStackTrace()
                });
            }
            throw error;
        }
    }

    private async _createResource(
        resourceType: Constants.ResourceType,
        name: string | null,
        payload: {},
        operation: 'create' | 'update'
    ): Promise<{}> {
        let status: number;
        return new Promise<IAzureResource>(async (resolve, reject) => {
            const headers = await this.getDefaultHeadersAsync();
            const url = resourceType && name ? this.createSingleResourceUri(resourceType, name) : this.getBaseUri();
            this.httpClient
                .put(url, JSON.stringify(payload), {
                    headers,
                    params: this.getDefaultParams(null, this._getApiVersion(resourceType))
                })
                .pipe(retryWhen(HttpUtil.defaultRetryPolicy))
                .toPromise()
                .then(async (resp) => {
                    status = resp.status;
                    if (status === 202 || (resourceType === 'workspace' && status === 201)) {
                        const finalResp = await this._pollForCompletion(resp, resourceType);

                        // See https://docs.microsoft.com/azure/azure-resource-manager/resource-manager-async-operations#azure-asyncoperation-request-and-response.
                        const body = finalResp.json();
                        if (body && body.status === 'Failed') {
                            throw finalResp;
                        }

                        resolve(finalResp.json());
                    } else {
                        resolve(resp.json());
                    }
                })
                .catch((reason) => {
                    this.loggerHelper.logRequestError(
                        this.className,
                        '_createResource: Error while calling url',
                        reason,
                        { createResourceUrl: url }
                    );
                    status = reason.status;
                    reject(reason);
                })
                .then(() => {
                    this.logger.logEvent(
                        new CrudEvent(
                            operation,
                            name || this.urlService.resourceName,
                            resourceType,
                            '',
                            undefined,
                            status.toString()
                        )
                    );
                });
        });
    }
}
